// Initialize Sentry before anything else
import "./instrument";
import "./index.css";

import { QueryClientProvider } from "@tanstack/react-query";
import { AuthKitProvider } from "@workos-inc/authkit-react";
import { setDefaultOptions } from "date-fns/setDefaultOptions";
import { NuqsAdapter } from "nuqs/adapters/react-router/v6";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { queryClient } from "@/shared/config/queryClient";
import { ContrastProvider } from "@/shared/hooks/useContrast";
import { DarkModeProvider } from "@/shared/hooks/useDarkMode";
import { PaletteProvider } from "@/shared/hooks/usePalette";
import { TextSizeProvider } from "@/shared/hooks/useTextSize";
import { getWeekStartsOn } from "@/shared/utils/firm";
import App from "./App";

// Make all date-fns week functions (startOfWeek, endOfWeek, etc.) follow the
// firm's week-start setting by default. Calendar views and "this week" / "last week"
// semantics pick this up automatically.
setDefaultOptions({ weekStartsOn: getWeekStartsOn() });

// Capture true screen height before any keyboard appears.
// Used by full-screen overlays (e.g. combobox picker) via --app-height.
// Updated on orientation change but intentionally NOT on resize, so the
// keyboard appearing never shrinks the value.
function setAppHeight() {
	document.documentElement.style.setProperty(
		"--stable-viewport-height",
		`${window.innerHeight}px`,
	);
}
setAppHeight();
window.addEventListener("orientationchange", () => {
	// Wait for the browser to finish rotating before sampling
	setTimeout(setAppHeight, 100);
});

const clientId = import.meta.env.VITE_WORKOS_CLIENT_ID;

createRoot(document.getElementById("root")!).render(
	<StrictMode>
		<AuthKitProvider clientId={clientId} redirectUri={window.location.origin}>
			<QueryClientProvider client={queryClient}>
				<DarkModeProvider>
					<PaletteProvider>
						<ContrastProvider>
							<TextSizeProvider>
								<BrowserRouter>
									<NuqsAdapter>
										<App />
									</NuqsAdapter>
								</BrowserRouter>
							</TextSizeProvider>
						</ContrastProvider>
					</PaletteProvider>
				</DarkModeProvider>
			</QueryClientProvider>
		</AuthKitProvider>
	</StrictMode>,
);

// UNREGISTER any existing service workers - they were causing blank page issues
// by serving stale cached content that never updated after deployments.
if ("serviceWorker" in navigator) {
	navigator.serviceWorker.getRegistrations().then((registrations) => {
		for (const registration of registrations) {
			registration.unregister().then((success) => {
				if (success) {
					console.log("Service worker unregistered successfully");
				}
			});
		}
	});
}
